import "./GpsTriggering.css";

import { Controller } from "@hotwired/stimulus";
import type { SmartGuideBrowserLocation } from "../BrowserLocation/SmartGuideBrowserLocation";
import {
    smartGuideLocationBroker,
    type SmartGuideLocationBrokerSubscription,
    type SmartGuideLocationProfile
} from "../BrowserLocation/SmartGuideLocationBroker";
import {
    applyMedia,
    getMediaRevision,
    showPlayer,
    updatePlaybackState,
    type PersistentAudioMedia
} from "../PersistentAudioPlayer/PersistentAudioPlayerUi";
import { formatTranslation, webTranslations } from "../webTranslations";
import { GpsTriggerAudioActivation } from "./GpsTriggerAudioActivation";
import { parseGpsTriggerContext, type GpsTriggerContext, type GpsTriggerItem } from "./GpsTriggerContextParser";
import { GpsTriggerEngine } from "./GpsTriggerEngine";
import {
    GpsTriggerMediaCoordinator,
    type GpsTriggerMediaOutcome,
    type GpsTriggerPlayerAdapter
} from "./GpsTriggerMediaCoordinator";
import { GpsTriggerPrecisionPolicy } from "./GpsTriggerPrecisionPolicy";
import { GpsTriggerSamplingPolicy } from "./GpsTriggerSamplingPolicy";
import { GpsTriggerVisitedStore } from "./GpsTriggerVisitedStore";
import { getGpsTriggerVisitedStore } from "./GpsTriggerVisitedState";
import { scopeWebIntegrationStorageKey } from "../../WebIntegrations/Navigation/WebIntegrationStorageScope";
import { getTourPoiSelectionState } from "../TourTray/TourPoiSelectionState";

const subscriptionId = "gps-triggering";
const revalidationAgeMs = 5 * 60 * 1_000;
const transientSampleRetryDelayMs = 30_000;

type GpsTriggerPageDescriptor = {
    enabled: boolean;
    mode: "destination" | "tour";
    contextKey: string;
    contextUrl: string | null;
    destinationId: string;
    language: string;
    tourId: string | null;
    tourType: string | null;
    pageScope: string | null;
    continuityPolicy: string | null;
};

type NotificationAction = "navigate" | "play" | "retry-play";

export default class GpsTriggeringController extends Controller<HTMLElement> {
    private activeDescriptor?: GpsTriggerPageDescriptor;
    private activeContext?: GpsTriggerContext;
    private engine?: GpsTriggerEngine;
    private precisionPolicy?: GpsTriggerPrecisionPolicy;
    private readonly samplingPolicy = new GpsTriggerSamplingPolicy();
    private latestLocation?: SmartGuideBrowserLocation;
    private unsubscribeLocation?: () => void;
    private locationProfile: SmartGuideLocationProfile = "balanced";
    private sampleTimeoutId?: ReturnType<typeof setTimeout>;
    private sampleRequestPending = false;
    private requestAbort?: AbortController;
    private requestGeneration = 0;
    private requestPending = false;
    private contextNotFound = false;
    private navigationPending = false;
    private validatedAt = 0;
    private etag?: string;
    private notificationItem?: GpsTriggerItem;
    private notificationAction?: NotificationAction;
    private lifecycleGeneration = 0;
    private readonly audioActivation = new GpsTriggerAudioActivation();

    connect(): void {
        this.lifecycleGeneration++;
        document.addEventListener("turbo:visit", this.onTurboVisit);
        document.addEventListener("turbo:load", this.onTurboLoad);
        document.addEventListener("visibilitychange", this.onVisibilityChanged);
        document.addEventListener("click", this.onAudioActivation, true);
        document.addEventListener("keydown", this.onAudioActivation, true);
        this.applyPageDescriptor();
    }

    disconnect(): void {
        const generation = ++this.lifecycleGeneration;
        setTimeout(() => {
            if (generation !== this.lifecycleGeneration) {
                return;
            }

            document.removeEventListener("turbo:visit", this.onTurboVisit);
            document.removeEventListener("turbo:load", this.onTurboLoad);
            document.removeEventListener("visibilitychange", this.onVisibilityChanged);
            document.removeEventListener("click", this.onAudioActivation, true);
            document.removeEventListener("keydown", this.onAudioActivation, true);
            this.deactivate();
        }, 0);
    }

    dismiss(): void {
        this.hideNotification();
    }

    openDetail(event: Event): void {
        event.preventDefault();
        const item = this.notificationItem;
        if (!item?.detailUrl) {
            return;
        }

        this.hideNotification();
        const turbo = (globalThis as typeof globalThis & { Turbo?: { visit: (url: string) => void } }).Turbo;
        if (turbo) {
            turbo.visit(item.detailUrl);
        } else {
            globalThis.location.assign(item.detailUrl);
        }
    }

    async runNotificationAction(event: Event): Promise<void> {
        event.preventDefault();
        const item = this.notificationItem;
        if (!item || !this.notificationAction) {
            return;
        }

        if (this.notificationAction === "navigate") {
            this.openDetail(event);
            return;
        }

        const action = this.notificationAction;
        const generation = this.requestGeneration;
        const scope = this.activeDescriptor?.contextKey;
        const selection = getTourPoiSelectionState(document);
        const selectionRevision = selection?.getRevision();
        try {
            await this.player.playItem(item);
            if (this.notificationItem === item && this.notificationAction === action && generation === this.requestGeneration && scope === this.activeDescriptor?.contextKey) {
                if (scope && selection?.getRevision() === selectionRevision) this.selectTriggeredTourPoi(item, scope);
                this.hideNotification();
            }
        } catch {
            // Keep the user-initiated playback action available for another attempt.
        }
    }

    private readonly onTurboVisit = (): void => {
        this.navigationPending = true;
    };

    private readonly onTurboLoad = (): void => {
        if (this.navigationPending) {
            this.contextNotFound = false;
            this.navigationPending = false;
        }
        this.applyPageDescriptor();
    };

    private readonly onAudioActivation = (): void => {
        const cueSourceUrl = this.element.dataset.gpsTriggerCueSrc?.trim();
        const storyAudio = document.querySelector<HTMLAudioElement>(
            "audio[data-persistent-audio-player-target='audio']"
        );
        if (!cueSourceUrl || !storyAudio) {
            return;
        }

        this.audioActivation.arm(cueSourceUrl, storyAudio);
    };

    private readonly onVisibilityChanged = (): void => {
        if (document.visibilityState === "hidden") {
            this.engine?.clearEnteredZones();
            this.cancelScheduledSample();
            return;
        }

        if (this.activeContext && this.engine?.remainingCount && this.locationProfile === "observe-only") {
            void this.requestLocationSample();
        }

        if (this.activeDescriptor &&
            (!this.activeContext || (this.validatedAt > 0 && Date.now() - this.validatedAt >= revalidationAgeMs))) {
            void this.loadContext(this.activeDescriptor, this.activeContext !== undefined, this.requestGeneration);
        }
    };

    private applyPageDescriptor(): void {
        if (document.documentElement.hasAttribute("data-turbo-preview")) return;
        if (this.contextNotFound) return;
        if (/^\/profile(?:\/|$)/.test(window.location.pathname) && this.activeDescriptor) return;
        const descriptor = readPageDescriptor(document);
        const retainedTour = descriptor ? this.shouldRetainTourForSights(descriptor) : false;
        const scope = descriptor?.mode === "tour" ? descriptor.contextKey : retainedTour ? this.activeDescriptor?.contextKey : null;
        getTourPoiSelectionState(document)?.activateScope(scope ?? null);
        if (!descriptor?.enabled || !descriptor.contextUrl) {
            this.deactivate();
            return;
        }

        if (retainedTour) {
            return;
        }

        if (this.activeDescriptor?.contextKey === descriptor.contextKey) {
            if (!this.activeContext && !this.requestPending) {
                void this.loadContext(this.activeDescriptor, false, this.requestGeneration);
            }
            return;
        }

        this.activate(descriptor);
    }

    private shouldRetainTourForSights(descriptor: GpsTriggerPageDescriptor): boolean {
        return descriptor.continuityPolicy === "retain-same-destination-tour" &&
            this.activeDescriptor?.mode === "tour" &&
            this.activeDescriptor.destinationId === descriptor.destinationId &&
            this.activeDescriptor.language === descriptor.language &&
            (this.activeContext !== undefined || this.requestPending);
    }

    private activate(descriptor: GpsTriggerPageDescriptor): void {
        this.stopCurrentContext();
        this.activeDescriptor = descriptor;
        this.precisionPolicy = new GpsTriggerPrecisionPolicy();
        const generation = ++this.requestGeneration;
        void this.loadContext(descriptor, false, generation);
    }

    private deactivate(): void {
        this.stopCurrentContext();
        this.activeDescriptor = undefined;
        this.latestLocation = undefined;
        this.hideNotification();
        this.requestGeneration++;
    }

    private stopCurrentContext(): void {
        this.requestAbort?.abort();
        this.requestAbort = undefined;
        this.requestPending = false;
        this.cancelScheduledSample();
        this.sampleRequestPending = false;
        this.releaseLocationSubscription();
        this.activeContext = undefined;
        this.engine = undefined;
        this.precisionPolicy = undefined;
        this.etag = undefined;
        this.validatedAt = 0;
        this.hideNotification();
    }

    private ensureLocationSubscription(): void {
        if (this.unsubscribeLocation || !this.activeDescriptor) {
            return;
        }

        const subscribedProfile = this.locationProfile;
        const subscription: SmartGuideLocationBrokerSubscription = {
            id: subscriptionId,
            profile: subscribedProfile,
            onLocation: this.onLocation,
            onError: this.onLocationError
        };
        this.unsubscribeLocation = smartGuideLocationBroker.subscribe(subscription);
        if (!this.engine?.remainingCount) {
            this.releaseLocationSubscription();
        } else if (this.locationProfile !== subscribedProfile) {
            smartGuideLocationBroker.updateProfile(subscriptionId, this.locationProfile);
        }
    }

    private releaseLocationSubscription(): void {
        this.unsubscribeLocation?.();
        this.unsubscribeLocation = undefined;
    }

    private async loadContext(
        descriptor: GpsTriggerPageDescriptor,
        revalidate: boolean,
        generation: number
    ): Promise<void> {
        if (this.requestPending || generation !== this.requestGeneration) {
            return;
        }

        this.requestPending = true;
        const abort = new AbortController();
        this.requestAbort = abort;
        const headers = new Headers();
        if (revalidate && this.etag) {
            headers.set("If-None-Match", this.etag);
        }

        try {
            const response = await fetch(descriptor.contextUrl!, {
                cache: "no-cache",
                credentials: "same-origin",
                headers,
                signal: abort.signal
            });
            if (generation !== this.requestGeneration || abort.signal.aborted) {
                return;
            }

            if (response.status === 304 && this.activeContext) {
                this.validatedAt = Date.now();
                return;
            }
            if (!response.ok) {
                if (response.status === 404) {
                    this.deactivate();
                    this.contextNotFound = true;
                }
                return;
            }

            const context = parseGpsTriggerContext(await response.json());
            if (!context || context.contextKey !== descriptor.contextKey || generation !== this.requestGeneration) {
                return;
            }

            this.installContext(context, response.headers.get("ETag") ?? undefined);
        } catch (error) {
            if (!(error instanceof DOMException && error.name === "AbortError")) {
                // A later visibility restore or page reactivation provides the retry opportunity.
            }
        } finally {
            if (generation === this.requestGeneration && this.requestAbort === abort) {
                this.requestPending = false;
                this.requestAbort = undefined;
            }
        }

        const currentDescriptor = readPageDescriptor(document);
        if (!this.activeContext &&
            this.activeDescriptor?.mode === "tour" &&
            currentDescriptor?.continuityPolicy === "retain-same-destination-tour") {
            this.applyPageDescriptor();
        }
    }

    private installContext(context: GpsTriggerContext, etag?: string): void {
        this.activeContext = context;
        this.etag = etag;
        this.validatedAt = Date.now();
        this.engine = new GpsTriggerEngine(
            context,
            getGpsTriggerVisitedStore(this.element, safeLocalStorage(), scopeWebIntegrationStorageKey(GpsTriggerVisitedStore.storageKey))
        );
        this.precisionPolicy = new GpsTriggerPrecisionPolicy();
        this.locationProfile = "balanced";

        if (this.engine.remainingCount === 0) {
            this.cancelScheduledSample();
            this.releaseLocationSubscription();
            return;
        }

        this.ensureLocationSubscription();
        if (this.latestLocation) {
            this.processLocation(this.latestLocation);
        }
    }

    private readonly onLocation = (location: SmartGuideBrowserLocation): void => {
        this.latestLocation = location;
        this.processLocation(location);
    };

    private processLocation(location: SmartGuideBrowserLocation): void {
        const engine = this.engine;
        const descriptor = this.activeDescriptor;
        if (!engine || !descriptor) {
            return;
        }

        if (isPrecisionEligibleLocation(location)) {
            const distance = engine.distanceToNearestUnvisitedZoneAnywhere(location);
            const profile = this.precisionPolicy?.update(distance, location.accuracy, location.timestamp) ?? "balanced";
            if (profile === "precise") {
                this.cancelScheduledSample();
                this.updateLocationProfile("precise");
            } else {
                const sampleDelay = this.samplingPolicy.nextSampleDelayMs(distance, location.accuracy);
                if (sampleDelay === undefined) {
                    this.cancelScheduledSample();
                    this.updateLocationProfile("balanced");
                } else {
                    this.updateLocationProfile("observe-only");
                    this.scheduleLocationSample(sampleDelay);
                }
            }
        }

        const item = engine.evaluate(location);
        if (engine.remainingCount === 0) {
            this.cancelScheduledSample();
            this.releaseLocationSubscription();
        }
        if (item) {
            const generation = this.requestGeneration;
            void this.handleTriggeredItem(descriptor.mode, item, generation);
        }
    }

    private updateLocationProfile(profile: SmartGuideLocationProfile): void {
        if (this.locationProfile === profile) {
            return;
        }

        this.locationProfile = profile;
        if (this.unsubscribeLocation) {
            smartGuideLocationBroker.updateProfile(subscriptionId, this.locationProfile);
        }
    }

    private scheduleLocationSample(delayMs: number): void {
        this.cancelScheduledSample();
        if (document.visibilityState === "hidden" || !this.activeContext || !this.engine?.remainingCount) {
            return;
        }

        this.sampleTimeoutId = globalThis.setTimeout(() => {
            this.sampleTimeoutId = undefined;
            void this.requestLocationSample();
        }, delayMs);
    }

    private cancelScheduledSample(): void {
        if (this.sampleTimeoutId !== undefined) {
            globalThis.clearTimeout(this.sampleTimeoutId);
            this.sampleTimeoutId = undefined;
        }
    }

    private async requestLocationSample(): Promise<void> {
        if (this.sampleRequestPending ||
            document.visibilityState === "hidden" ||
            !this.activeContext ||
            !this.engine?.remainingCount ||
            this.locationProfile !== "observe-only") {
            return;
        }

        this.sampleRequestPending = true;
        const generation = this.requestGeneration;
        try {
            await smartGuideLocationBroker.requestFix("gps-triggering-sample", {
                maxAgeMs: 0,
                profile: "balanced"
            });
        } catch (error) {
            if (generation !== this.requestGeneration) {
                return;
            }

            const locationError = error as GeolocationPositionError | Error;
            const denied = "code" in locationError && locationError.code === 1;
            if (denied) {
                this.onLocationError(locationError);
            } else {
                this.scheduleLocationSample(transientSampleRetryDelayMs);
            }
        } finally {
            if (generation === this.requestGeneration) {
                this.sampleRequestPending = false;
            }
        }
    }

    private async handleTriggeredItem(
        mode: "destination" | "tour",
        item: GpsTriggerItem,
        generation: number
    ): Promise<void> {
        if (generation !== this.requestGeneration) return;
        if (mode === "tour" && this.activeDescriptor) this.selectTriggeredTourPoi(item, this.activeDescriptor.contextKey);
        const playableItem = mode === "tour" && item.isSubscriberOnly
            ? { ...item, audio: null }
            : item;
        const coordinator = new GpsTriggerMediaCoordinator(
            this.player,
            () => this.playCue(),
            duration => navigator.vibrate?.(duration)
        );
        const outcomePromise = coordinator.handle(
            mode,
            playableItem,
            () => generation === this.requestGeneration,
            this.audioActivation.hasReceivedInteraction
        );
        if (mode === "destination") {
            this.showNotification(item, "destination");
        }

        const outcome = await outcomePromise;
        if (generation !== this.requestGeneration) {
            return;
        }

        if (mode === "tour") {
            this.presentOutcome(outcome, item);
        }
    }

    private presentOutcome(outcome: GpsTriggerMediaOutcome, item: GpsTriggerItem): void {
        if (outcome === "destination-banner") {
            this.showNotification(item, "destination");
        } else if (outcome === "tour-autoplay-blocked") {
            this.showNotification(item, "autoplay-fallback");
        }
    }

    private selectTriggeredTourPoi(item: GpsTriggerItem, tourContextKey: string): void {
        if (item.isNavigation || this.activeDescriptor?.mode !== "tour" || this.activeDescriptor.contextKey !== tourContextKey ||
            !this.activeContext?.items.some(candidate => candidate.id === item.id && !candidate.isNavigation)) return;
        getTourPoiSelectionState(document)?.select({ tourContextKey, poiId: item.id, origin: "gps" });
    }

    private showNotification(item: GpsTriggerItem, kind: "destination" | "autoplay-fallback"): void {
        const notification = this.query<HTMLElement>("[data-gps-trigger-notification]");
        if (!notification) {
            return;
        }

        this.notificationItem = item;
        notification.dataset.gpsTriggerNotificationKind = kind;
        notification.classList.toggle("gps-trigger-notification--navigation", item.isNavigation);

        const message = kind === "autoplay-fallback"
            ? formatTranslation(webTranslations.gpsTriggeredPoiPlaybackBlockedFormat, item.name)
            : formatTranslation(webTranslations.gpsTriggeredPoiArrivalFormat, item.name);
        this.setText("[data-gps-trigger-message]", message);

        const image = this.query<HTMLImageElement>("[data-gps-trigger-image]");
        if (image) {
            if (item.imageUrl) {
                image.src = item.imageUrl;
                image.alt = item.name;
                image.hidden = false;
            } else {
                image.removeAttribute("src");
                image.alt = "";
                image.hidden = true;
            }
        }
        const imageLink = this.query<HTMLElement>("[data-gps-trigger-image-link]");
        if (item.detailUrl) {
            imageLink?.setAttribute("href", item.detailUrl);
        } else {
            imageLink?.removeAttribute("href");
        }

        const shouldPlay = kind === "autoplay-fallback" || (!!item.audio && !item.isSubscriberOnly);
        this.notificationAction = shouldPlay
            ? kind === "autoplay-fallback" ? "retry-play" : "play"
            : "navigate";
        this.setText(
            "[data-gps-trigger-action-label]",
            shouldPlay ? webTranslations.gpsTriggeredPoiPlayStory : webTranslations.learnMore
        );
        notification.classList.toggle("gps-trigger-notification--play-action", shouldPlay);
        notification.classList.toggle("gps-trigger-notification--locked", item.isSubscriberOnly);
        notification.hidden = false;
    }

    private hideNotification(): void {
        const notification = this.query<HTMLElement>("[data-gps-trigger-notification]");
        if (notification) {
            notification.hidden = true;
            delete notification.dataset.gpsTriggerNotificationKind;
        }
        this.notificationItem = undefined;
        this.notificationAction = undefined;
    }

    private readonly onLocationError = (error: GeolocationPositionError | Error): void => {
        const denied = "code" in error && error.code === 1;
        if (denied) {
            this.cancelScheduledSample();
            this.releaseLocationSubscription();
        }
    };

    private async playCue(): Promise<void> {
        const source = this.element.dataset.gpsTriggerCueSrc?.trim();
        if (!source) {
            return;
        }
        await this.audioActivation.playCue(source);
    }

    private get player(): GpsTriggerPlayerAdapter {
        return new PersistentAudioPlayerAdapter();
    }

    private query<T extends Element>(selector: string): T | null {
        return this.element.querySelector<T>(selector);
    }

    private setText(selector: string, value: string): void {
        const element = this.query<HTMLElement>(selector);
        if (element) {
            element.textContent = value;
        }
    }
}

class PersistentAudioPlayerAdapter implements GpsTriggerPlayerAdapter {
    get activeMediaRevision(): number {
        return this.audio ? getMediaRevision(this.audio) : 0;
    }

    get activeMediaId(): string | null {
        return this.audio?.dataset.mediaId?.trim() || null;
    }

    get isPlaying(): boolean {
        return !!this.audio && !this.audio.paused;
    }

    pause(): void {
        this.audio?.pause();
        this.update();
    }

    async resume(): Promise<void> {
        const audio = this.requiredAudio;
        await audio.play();
        this.update();
    }

    async playItem(item: GpsTriggerItem): Promise<void> {
        this.prepareItem(item);
        showPlayer(this.requiredRoot);
        const audio = this.requiredAudio;
        try {
            await audio.play();
        } finally {
            this.update();
        }
    }

    prepareItem(item: GpsTriggerItem): void {
        if (!item.audio) {
            throw new Error("The triggered POI has no playable story.");
        }

        const root = this.requiredRoot;
        const audio = this.requiredAudio;
        const media: PersistentAudioMedia = {
            id: item.audio.id,
            url: item.audio.url,
            title: item.name,
            artist: "SmartGuide",
            artworkUrl: item.imageUrl ?? undefined,
            detailUrl: item.detailUrl
        };
        applyMedia(root, audio, media);
    }

    private update(): void {
        if (this.root && this.audio) {
            updatePlaybackState(this.root, this.audio);
        }
    }

    private get root(): HTMLElement | null {
        return document.querySelector<HTMLElement>("[data-persistent-audio-player]");
    }

    private get audio(): HTMLAudioElement | null {
        return this.root?.querySelector<HTMLAudioElement>("audio[data-persistent-audio-player-target='audio']") ?? null;
    }

    private get requiredRoot(): HTMLElement {
        const root = this.root;
        if (!root) {
            throw new Error("The persistent audio player is unavailable.");
        }
        return root;
    }

    private get requiredAudio(): HTMLAudioElement {
        const audio = this.audio;
        if (!audio) {
            throw new Error("The persistent audio element is unavailable.");
        }
        return audio;
    }
}

function readPageDescriptor(root: ParentNode): GpsTriggerPageDescriptor | undefined {
    const script = root.querySelector<HTMLScriptElement>("[data-smartguide-gps-trigger-context]");
    if (!script?.textContent) {
        return undefined;
    }

    try {
        const value = JSON.parse(script.textContent) as unknown;
        if (!isObject(value) ||
            typeof value.enabled !== "boolean" ||
            (value.mode !== "destination" && value.mode !== "tour") ||
            !isNonEmptyString(value.contextKey) ||
            !isNonEmptyString(value.destinationId) ||
            !isNonEmptyString(value.language)) {
            return undefined;
        }

        return {
            enabled: value.enabled,
            mode: value.mode,
            contextKey: value.contextKey,
            contextUrl: nullableString(value.contextUrl),
            destinationId: value.destinationId,
            language: value.language,
            tourId: nullableString(value.tourId),
            tourType: nullableString(value.tourType),
            pageScope: nullableString(value.pageScope),
            continuityPolicy: nullableString(value.continuityPolicy)
        };
    } catch {
        return undefined;
    }
}

function safeLocalStorage(): Storage | undefined {
    try {
        return globalThis.localStorage;
    } catch {
        return undefined;
    }
}

function nullableString(value: unknown): string | null {
    return typeof value === "string" ? value : null;
}

function isNonEmptyString(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0;
}

function isObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object";
}

function isPrecisionEligibleLocation(location: SmartGuideBrowserLocation): location is SmartGuideBrowserLocation & { accuracy: number } {
    return location.source === "gps" &&
        typeof location.accuracy === "number" &&
        Number.isFinite(location.accuracy) &&
        location.accuracy >= 0 &&
        Number.isFinite(location.timestamp) &&
        Date.now() - location.timestamp <= 30_000;
}
